import { Injectable, Output, EventEmitter } from '@angular/core';
import { HubConnection, HubConnectionState, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { Log } from 'src/app/core/log';

@Injectable({
  providedIn: 'root'
})
export class SignalrService {

  @Output() eventReceived: EventEmitter<SignalrEvent> = new EventEmitter();

  private hubConnection: HubConnection;
  private connectCallback: any;
  private didDisconnect = false;
  private busy = false;


  constructor() { }

  get isConnected() { return (this.hubConnection != null && this.hubConnection.state === HubConnectionState.Connected); }
  get isBusy() { return this.busy; }



  async initialize(onConnect: any, onDisconnect: any) {
    try {
      // Store the on-connect function
      this.connectCallback = onConnect;
      if (this.hubConnection == null) {
        // Instantiate the hub connection
        Log.debug('Creating hub connection.');
        this.hubConnection = new HubConnectionBuilder()
          .withUrl(AppSettings.hubURL, {
             skipNegotiation: true,
             transport: HttpTransportType.WebSockets
          })
          .configureLogging(LogLevel.Information)
          .build();


        // Bind to the on-close event
        this.hubConnection.onclose(data => this.hubDisconnect(onDisconnect, data));
      }
      // Okay, the hub has been setup.  Lets just try connecting
      if (this.hubConnection.state === HubConnectionState.Disconnected) {
        await this.connect();
      } else {
        Log.debug('Hub connection already connected. No need to connect again.');
        if (this.connectCallback != null) {
          this.connectCallback();
        }
      }
      return this.hubConnection;
    } catch (error) {
      console.error('Auction hub error during initialize.', error);
      // throw error;
    }
    return null;
  }

  async runCommand(command: string, data: any) {
    if (this.isConnected === true) {
      try {
        return await this.hubConnection.invoke(command, data);
      } catch (error) {
        console.error('Auction hub error during connection.', error);
        throw error;
      }
    } else {
      console.error('Error running command. SignalR not connected');
      throw new Error('SignalR not connected to server');
    }
  }

  async disconnect() {
    try {
      // Set that we are explicitly disconnecting
      this.didDisconnect = true;
      // Only stop the connection if already connected
      if (this.isConnected === true) {
        await this.hubConnection.stop();
        Log.debug('Auction hub disconnected successfully!');
      }
    } catch (error) {
      console.error('Auction hub error during disconnect.', error);
    }
  }


  addEventHandlers(events: string[]) {
    for (const event of events) {
      this.hubConnection.on(event, data => {
        Log.debug('Hub connection event.', event);
        this.eventReceived.emit({ event, data });
      });
    }
  }

  addEventHandler(event: string) {
    this.addEventHandlers([event]);
  }

  removeEventHandler(event: string) {
    this.hubConnection.on(event, undefined);
  }

  retryConnection() {
    return this.connect();
  }

  private hubDisconnect(diconnectCallback: any, data: any) {
    if (diconnectCallback != null) {
      diconnectCallback();
    }
    Log.debug(`Hub Connection closed. User Closed: ${this.didDisconnect}`, data);
    if (this.didDisconnect === false) { this.reconnect(); }
  }

  private async connect(throwException: boolean = true) {
    if (this.hubConnection == null) {
      // The hub connection was null.. Lets initialize it
      console.error('SignalR connect was null. Running initialize instead.');
      await this.initialize(this.connectCallback, null);
    } else if (this.hubConnection.state === HubConnectionState.Disconnected) {
      try {
        this.busy = true;
        await this.hubConnection.start();
        Log.debug('Auction hub connected successfully!');
        this.didDisconnect = false;
        if (this.connectCallback != null) {
          this.connectCallback();
        }
      } catch (error) {
        console.error('Auction hub error during connection.', error);
        if (throwException) { throw error; }
      } finally {
        this.busy = false;
      }
    } else {
      console.error('Unable to connect to hub as connection is not in disconnected state.');
    }
  }


  private async reconnect() {
    // lets try connecting right away
    Log.debug('Hub connection closed. Attempting reconnects...');
    await this.connect(false);
    let retries = 12;
    const retryInterval = setInterval(async () => {
      // if connected or out of retries.. lets leave this
      if (this.isConnected === true || retries === 0) {
        clearInterval(retryInterval);
        return;
      } else if (this.busy === false) {
        // if we aren't trying to reconnect, then lets try
        await this.connect(false);
        retries -= 1;
        Log.debug(`Attempting to reconnect. Retries remaining: ${retries}`);
      }
    }, 2000);
    if (retries === 0) {
      Log.debug('Reconnect retries exhausted. Unable to reconnect. Stopping reconnect loop.');
    } else {
      Log.debug('Connection re-established. Stopping reconnect loop.');
    }
  }
}

export interface SignalrEvent {
  event: string;
  data: any;
}
